import React, { useState, useRef } from 'react';
import { DataManager } from '../../services/dataManager';
import GroupCounselingModal from './GroupCounselingModal';

/**
 * ToolkitView - Strumenti scaricabili per il counselor
 * 
 * Genera documenti PDF professionali stampabili:
 * 1. Questionario di inizio percorso
 * 2. Questionario di fine percorso
 * 3. Template Ruota della Vita
 * 4. Template Scrittura Storia
 */

const BRAND = {
    primary: '#335d73',
    light: '#85a8bf',
    soft: '#5c8aa8',
    dark: '#24485d',
    bg: '#f0f5f8',
    accent: '#6b92ab',
};

// ============================================================
// PDF GENERATION UTILITY
// ============================================================
const generatePrintableHTML = (title, content, subtitle = '') => {
    return `
<!DOCTYPE html>
<html lang="it">
<head>
    <meta charset="UTF-8">
    <title>${title} - AwarenessTool</title>
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Public+Sans:wght@300;400;500;600;700&display=swap');
        
        * { margin: 0; padding: 0; box-sizing: border-box; }
        
        @page {
            size: A4 portrait;
            margin: 18mm 16mm 22mm 16mm;
        }
        
        body {
            font-family: 'Public Sans', -apple-system, sans-serif;
            color: #1e293b;
            line-height: 1.6;
            font-size: 11pt;
            background: white;
            width: 178mm;
            margin: 0 auto;
        }
        
        .page { 
            page-break-after: always;
            width: 100%;
            max-width: 178mm;
        }
        .page:last-child {
            page-break-after: avoid;
        }
        
        svg {
            max-width: 100%;
            height: auto;
        }
        
        /* HEADER */
        .doc-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            border-bottom: 3px solid ${BRAND.primary};
            padding-bottom: 12px;
            margin-bottom: 24px;
        }
        .doc-header h1 {
            font-size: 20pt;
            font-weight: 700;
            color: ${BRAND.dark};
            letter-spacing: -0.5px;
        }
        .doc-header .brand {
            font-size: 8pt;
            color: ${BRAND.soft};
            text-transform: uppercase;
            letter-spacing: 2px;
            font-weight: 600;
        }
        .doc-subtitle {
            font-size: 10pt;
            color: ${BRAND.soft};
            margin-bottom: 20px;
            font-style: italic;
        }
        
        /* CLIENT INFO BOX */
        .client-info {
            background: ${BRAND.bg};
            border: 1px solid #d1dde5;
            border-radius: 8px;
            padding: 16px 20px;
            margin-bottom: 24px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
        }
        .client-info .field {
            display: flex;
            flex-direction: column;
        }
        .client-info .field-label {
            font-size: 7pt;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            color: ${BRAND.soft};
            margin-bottom: 2px;
        }
        .client-info .field-value {
            border-bottom: 1px solid #c2d5e0;
            min-height: 22px;
            font-size: 10pt;
            padding: 2px 0;
        }
        .client-info .field-full {
            grid-column: 1 / -1;
        }
        
        /* SECTION */
        .section {
            margin-bottom: 22px;
        }
        .section-title {
            font-size: 12pt;
            font-weight: 700;
            color: ${BRAND.primary};
            margin-bottom: 12px;
            padding-bottom: 4px;
            border-bottom: 1.5px solid ${BRAND.light};
            display: flex;
            align-items: center;
            gap: 8px;
        }
        .section-icon {
            font-size: 14pt;
        }
        
        /* QUESTION */
        .question {
            margin-bottom: 16px;
        }
        .question-text {
            font-size: 10pt;
            font-weight: 600;
            color: #334155;
            margin-bottom: 6px;
        }
        .question-hint {
            font-size: 8pt;
            color: #94a3b8;
            font-style: italic;
            margin-bottom: 4px;
        }
        .answer-lines {
            border-bottom: 1px solid #e2e8f0;
            height: 28px;
        }
        .answer-lines.tall {
            height: 60px;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
        }
        .answer-lines.extra-tall {
            height: 100px;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
        }
        
        /* SCALE */
        .scale-container {
            display: flex;
            align-items: center;
            gap: 4px;
            margin-top: 6px;
        }
        .scale-item {
            width: 28px;
            height: 28px;
            border: 1.5px solid #cbd5e1;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 8pt;
            font-weight: 600;
            color: #64748b;
        }
        .scale-labels {
            display: flex;
            justify-content: space-between;
            font-size: 7pt;
            color: #94a3b8;
            margin-top: 4px;
        }
        
        /* WHEEL OF LIFE */
        .wheel-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 14px;
        }
        .wheel-area {
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            padding: 14px;
            background: #fafcfd;
        }
        .wheel-area-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 10px;
        }
        .wheel-area-icon {
            font-size: 20pt;
        }
        .wheel-area-name {
            font-weight: 700;
            font-size: 10pt;
            color: ${BRAND.dark};
        }
        .wheel-scale {
            display: flex;
            gap: 3px;
            justify-content: center;
        }
        .wheel-scale-item {
            width: 24px;
            height: 24px;
            border: 1.5px solid #cbd5e1;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 8pt;
            font-weight: 600;
            color: #64748b;
        }
        .wheel-notes {
            margin-top: 8px;
            border-top: 1px dashed #e2e8f0;
            padding-top: 6px;
            min-height: 22px;
        }
        .wheel-notes-label {
            font-size: 7pt;
            color: #94a3b8;
            text-transform: uppercase;
            letter-spacing: 1px;
        }
        
        /* WHEEL RADAR CHART (SVG) */
        .wheel-chart-container {
            display: flex;
            justify-content: center;
            margin: 20px 0;
        }
        
        /* STORY WRITING */
        .story-prompt {
            background: ${BRAND.bg};
            border-left: 4px solid ${BRAND.primary};
            padding: 14px 18px;
            border-radius: 0 8px 8px 0;
            margin-bottom: 14px;
        }
        .story-prompt p {
            font-size: 10pt;
            color: ${BRAND.dark};
            line-height: 1.7;
        }
        .story-lines {
            width: 100%;
        }
        .story-line {
            border-bottom: 1px solid #e2e8f0;
            height: 32px;
        }
        
        /* FOOTER */
        .doc-footer {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            text-align: center;
            font-size: 7pt;
            color: #94a3b8;
            padding: 8px 18mm;
            border-top: 1px solid #e2e8f0;
        }
        .doc-footer .tagline {
            font-weight: 600;
            color: ${BRAND.soft};
        }
        
        /* NUMBER CIRCLE */
        .q-number {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 20px;
            height: 20px;
            background: ${BRAND.primary};
            color: white;
            border-radius: 50%;
            font-size: 8pt;
            font-weight: 700;
            margin-right: 6px;
            flex-shrink: 0;
        }

        /* CHECKBOX GROUP */
        .checkbox-group {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 6px;
            margin-top: 6px;
        }
        .checkbox-item {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 9pt;
        }
        .checkbox-box {
            width: 14px;
            height: 14px;
            border: 1.5px solid #cbd5e1;
            border-radius: 3px;
            flex-shrink: 0;
        }

        @media print {
            body { 
                -webkit-print-color-adjust: exact; 
                print-color-adjust: exact;
                width: 178mm;
                margin: 0;
            }
            .no-print { display: none !important; }
            svg { max-width: 100%; height: auto; }
        }
    </style>
</head>
<body>
    <div class="no-print" style="position:fixed;top:0;left:0;right:0;z-index:9999;background:${BRAND.dark};padding:10px 20px;display:flex;align-items:center;justify-content:space-between;box-shadow:0 2px 12px rgba(0,0,0,0.3);">
        <span style="color:white;font-size:11pt;font-weight:600;">📄 ${title} — AwarenessTool</span>
        <div style="display:flex;gap:8px;">
            <button onclick="window.print()" style="padding:8px 20px;background:white;color:${BRAND.dark};border:none;border-radius:8px;font-weight:700;font-size:10pt;cursor:pointer;">🖨️ Stampa / Salva PDF</button>
            <button onclick="window.close()" style="padding:8px 16px;background:rgba(255,255,255,0.15);color:white;border:1px solid rgba(255,255,255,0.3);border-radius:8px;font-weight:600;font-size:10pt;cursor:pointer;">✕ Chiudi</button>
        </div>
    </div>
    <div style="padding-top:56px;">
    <div class="doc-footer">
        <span class="tagline">AwarenessTool</span> — La tecnologia a supporto del counseling
    </div>
    ${content}
    </div>
</body>
</html>`;
};

const downloadPDF = (html, filename) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
        alert('Popup bloccato. Abilita i popup per scaricare il documento.');
        return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
};

// ============================================================
// DOCUMENT GENERATORS
// ============================================================

const generateQuestionarioInizio = (info = {}) => {
    const content = `
    <div class="page">
        <div class="doc-header">
            <h1>📋 Questionario di Inizio Percorso</h1>
            <div class="brand">AwarenessTool</div>
        </div>
        <p class="doc-subtitle">Da compilare durante il primo colloquio conoscitivo</p>
        
        <div class="client-info">
            <div class="field">
                <span class="field-label">Nome e Cognome</span>
                <div class="field-value">${info.name || ''}</div>
            </div>
            <div class="field">
                <span class="field-label">Data</span>
                <div class="field-value">${info.date || ''}</div>
            </div>
            <div class="field">
                <span class="field-label">Età</span>
                <div class="field-value">${info.age ? (String(info.age).toLowerCase().includes('anni') ? info.age : `${info.age} anni`) : ''}</div>
            </div>
            <div class="field">
                <span class="field-label">Professione / Occupazione</span>
                <div class="field-value">${info.profession || ''}</div>
            </div>
            <div class="field field-full">
                <span class="field-label">Contatto (Email o Telefono)</span>
                <div class="field-value">${info.contact || ''}</div>
            </div>
        </div>
        
        <div class="section">
            <div class="section-title"><span class="section-icon">🎯</span> Motivazione e Aspettative</div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">1</span>Cosa ti ha spinto ad iniziare questo percorso di counseling?</div>
                <div class="answer-lines extra-tall"></div>
            </div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">2</span>Quali sono i tuoi obiettivi principali per questo percorso?</div>
                <div class="question-hint">Elenca 2-3 risultati concreti che vorresti raggiungere</div>
                <div class="answer-lines extra-tall"></div>
            </div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">3</span>Hai già avuto esperienze di counseling o percorsi simili in passato?</div>
                <div class="checkbox-group">
                    <div class="checkbox-item"><div class="checkbox-box"></div> Sì, counseling</div>
                    <div class="checkbox-item"><div class="checkbox-box"></div> Sì, psicoterapia</div>
                    <div class="checkbox-item"><div class="checkbox-box"></div> Sì, coaching</div>
                    <div class="checkbox-item"><div class="checkbox-box"></div> No, mai</div>
                </div>
                <div style="margin-top:8px">
                    <div class="question-hint">Se sì, racconta brevemente:</div>
                    <div class="answer-lines tall"></div>
                </div>
            </div>
        </div>
    </div>
    
    <div class="page">
        <div class="section">
            <div class="section-title"><span class="section-icon">💭</span> Qui ed Ora — Stato Attuale</div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">4</span>Come ti senti in questo momento della tua vita? Descrivi il tuo stato d'animo attuale.</div>
                <div class="answer-lines extra-tall"></div>
            </div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">5</span>Quali sono le principali aree di disagio o insoddisfazione?</div>
                <div class="answer-lines extra-tall"></div>
            </div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">6</span>In una scala da 1 a 10, quanto ti senti soddisfatto/a della tua vita in questo momento?</div>
                <div class="scale-container">
                    ${[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => `<div class="scale-item">${n}</div>`).join('')}
                </div>
                <div class="scale-labels">
                    <span>Per niente soddisfatto</span>
                    <span>Completamente soddisfatto</span>
                </div>
            </div>
        </div>
        
        <div class="section">
            <div class="section-title"><span class="section-icon">💪</span> Risorse e Punti di Forza</div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">7</span>Quali consideri le tue risorse personali e i tuoi punti di forza?</div>
                <div class="answer-lines extra-tall"></div>
            </div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">8</span>Cosa ti dà energia e motivazione nella vita quotidiana?</div>
                <div class="answer-lines tall"></div>
            </div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">9</span>Hai una rete di supporto (famiglia, amici, partner)?</div>
                <div class="answer-lines tall"></div>
            </div>
        </div>
        
        <div class="section">
            <div class="section-title"><span class="section-icon">📋</span> Informazioni Aggiuntive</div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">10</span>Ci sono temi o argomenti che consideri particolarmente importanti da affrontare?</div>
                <div class="answer-lines tall"></div>
            </div>

            <div class="question">
                <div class="question-text"><span class="q-number">11</span>Ci sono fattori medici o situazioni particolari di cui il counselor dovrebbe essere a conoscenza?</div>
                <div class="answer-lines tall"></div>
            </div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">12</span>C'è qualcosa che vorresti aggiungere e che non ti è stato chiesto?</div>
                <div class="answer-lines tall"></div>
            </div>
        </div>
        
        <div style="margin-top: 30px; display: flex; justify-content: space-between; align-items: flex-end;">
            <div>
                <div style="font-size: 8pt; color: #94a3b8; margin-bottom: 4px;">Firma del/la cliente</div>
                <div style="border-bottom: 1px solid #cbd5e1; width: 200px; height: 30px;"></div>
            </div>
            <div>
                <div style="font-size: 8pt; color: #94a3b8; margin-bottom: 4px;">Data</div>
                <div style="border-bottom: 1px solid #cbd5e1; width: 150px; height: 30px;"></div>
            </div>
        </div>
    </div>`;

    return generatePrintableHTML('Questionario di Inizio Percorso', content);
};

const generateQuestionarioFine = (info = {}) => {
    const content = `
    <div class="page">
        <div class="doc-header">
            <h1>🌳 Questionario di Fine Percorso</h1>
            <div class="brand">AwarenessTool</div>
        </div>
        <p class="doc-subtitle">Da compilare nell'ultimo incontro o durante la chiusura del percorso</p>
        
        <div class="client-info">
            <div class="field">
                <span class="field-label">Nome e Cognome</span>
                <div class="field-value">${info.name || ''}</div>
            </div>
            <div class="field">
                <span class="field-label">Data</span>
                <div class="field-value">${info.date || ''}</div>
            </div>
            <div class="field">
                <span class="field-label">Percorso iniziato il</span>
                <div class="field-value">${info.startDate || ''}</div>
            </div>
            <div class="field">
                <span class="field-label">N. incontri totali</span>
                <div class="field-value">${info.totalMeetings || ''}</div>
            </div>
        </div>
        
        <div class="section">
            <div class="section-title"><span class="section-icon">📊</span> Valutazione del Percorso</div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">1</span>Come valuti complessivamente il tuo percorso di counseling?</div>
                <div class="scale-container">
                    ${[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => `<div class="scale-item">${n}</div>`).join('')}
                </div>
                <div class="scale-labels">
                    <span>Non utile</span>
                    <span>Estremamente utile</span>
                </div>
            </div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">2</span>In che misura ritieni di aver raggiunto gli obiettivi che avevi all'inizio?</div>
                <div class="scale-container">
                    ${[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => `<div class="scale-item">${n}</div>`).join('')}
                </div>
                <div class="scale-labels">
                    <span>Per niente raggiunti</span>
                    <span>Completamente raggiunti</span>
                </div>
            </div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">3</span>In una scala da 1 a 10, quanto ti senti soddisfatto/a della tua vita in questo momento?</div>
                <div class="scale-container">
                    ${[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => `<div class="scale-item">${n}</div>`).join('')}
                </div>
                <div class="scale-labels">
                    <span>Per niente soddisfatto</span>
                    <span>Completamente soddisfatto</span>
                </div>
            </div>
        </div>
        
        <div class="section">
            <div class="section-title"><span class="section-icon">🌱</span> Cambiamenti Percepiti</div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">4</span>Quali cambiamenti hai notato in te dall'inizio del percorso ad oggi?</div>
                <div class="answer-lines extra-tall"></div>
            </div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">5</span>Quali nuove consapevolezze hai acquisito?</div>
                <div class="answer-lines extra-tall"></div>
            </div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">6</span>Cosa hai imparato di nuovo su di te?</div>
                <div class="answer-lines tall"></div>
            </div>
        </div>
    </div>
    
    <div class="page">
        <div class="section">
            <div class="section-title"><span class="section-icon">🔑</span> Momenti Chiave</div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">7</span>Qual è il momento più significativo che ricordi di questo percorso?</div>
                <div class="answer-lines extra-tall"></div>
            </div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">8</span>C'è stato qualcosa di particolarmente difficile che hai affrontato?</div>
                <div class="answer-lines tall"></div>
            </div>
        </div>
        
        <div class="section">
            <div class="section-title"><span class="section-icon">🚀</span> Prospettive Future</div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">9</span>Quali strumenti o strategie senti di aver acquisito per il futuro?</div>
                <div class="answer-lines extra-tall"></div>
            </div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">10</span>Ci sono aree della tua vita su cui vorresti continuare a lavorare autonomamente?</div>
                <div class="answer-lines tall"></div>
            </div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">11</span>Come immagini i prossimi mesi? Quali intenzioni porti con te?</div>
                <div class="answer-lines tall"></div>
            </div>
        </div>
        
        <div class="section">
            <div class="section-title"><span class="section-icon">💬</span> Feedback e Suggerimenti</div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">12</span>Come ti sei sentito/a nella relazione con il counselor?</div>
                <div class="answer-lines tall"></div>
            </div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">13</span>C'è qualcosa che vorresti suggerire o che avrebbe reso il percorso migliore?</div>
                <div class="answer-lines tall"></div>
            </div>
            
            <div class="question">
                <div class="question-text"><span class="q-number">14</span>Consiglieresti questo percorso ad altri? Perché?</div>
                <div class="answer-lines tall"></div>
            </div>
        </div>
        
        <div style="margin-top: 30px; display: flex; justify-content: space-between; align-items: flex-end;">
            <div>
                <div style="font-size: 8pt; color: #94a3b8; margin-bottom: 4px;">Firma del/la cliente</div>
                <div style="border-bottom: 1px solid #cbd5e1; width: 200px; height: 30px;"></div>
            </div>
            <div>
                <div style="font-size: 8pt; color: #94a3b8; margin-bottom: 4px;">Data</div>
                <div style="border-bottom: 1px solid #cbd5e1; width: 150px; height: 30px;"></div>
            </div>
        </div>
    </div>`;

    return generatePrintableHTML('Questionario di Fine Percorso', content);
};

const generateRuotaDellaVita = (info = {}) => {
    const n = 8;
    // Full-page radar SVG — large, fills the A4 page
    const cx = 240, cy = 240, maxR = 200;
    const gridCircles = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(level => {
        const r = (level / 10) * maxR;
        const isMajor = level % 2 === 0;
        return `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${isMajor ? '#b8c9d4' : '#e2eaf0'}" stroke-width="${level === 10 ? 2 : isMajor ? 1 : 0.5}"/>
                ${isMajor ? `<text x="${cx + 5}" y="${cy - r + 5}" font-size="10" fill="#7a97aa" font-weight="600">${level}</text>` : ''}`;
    }).join('');
    const axisLines = Array.from({ length: n }, (_, i) => {
        const angle = (Math.PI * 2 * i / n) - Math.PI / 2;
        const x2 = cx + maxR * Math.cos(angle);
        const y2 = cy + maxR * Math.sin(angle);
        return `<line x1="${cx}" y1="${cy}" x2="${x2}" y2="${y2}" stroke="#a4b8c6" stroke-width="0.8"/>`;
    }).join('');
    // Numbered labels with writing space
    const axisLabels = Array.from({ length: n }, (_, i) => {
        const angle = (Math.PI * 2 * i / n) - Math.PI / 2;
        const labelR = maxR + 24;
        const x = cx + labelR * Math.cos(angle);
        const y = cy + labelR * Math.sin(angle);
        const ta = Math.abs(Math.cos(angle)) < 0.15 ? 'middle' : Math.cos(angle) > 0 ? 'start' : 'end';
        return `<text x="${x}" y="${y}" text-anchor="${ta}" dominant-baseline="middle" font-size="13" font-weight="800" fill="${BRAND.dark}">${i + 1}.</text>
                <line x1="${x + (ta === 'end' ? -90 : ta === 'middle' ? -35 : 20)}" y1="${y + 9}" x2="${x + (ta === 'end' ? -5 : ta === 'middle' ? 35 : 105)}" y2="${y + 9}" stroke="#b8c9d4" stroke-width="0.8" stroke-dasharray="2,2"/>`;
    }).join('');
    const chartSVG = `<svg width="480" height="480" viewBox="0 0 480 480" xmlns="http://www.w3.org/2000/svg" style="display:block;margin:0 auto;">${gridCircles}${axisLines}${axisLabels}<circle cx="${cx}" cy="${cy}" r="3" fill="${BRAND.primary}"/></svg>`;

    // Spacious area rows with generous height
    const areaRows = Array.from({ length: n }, (_, i) => `
        <tr style="height:50px;">
            <td style="width:40px;text-align:center;vertical-align:middle;font-weight:800;color:${BRAND.primary};font-size:14pt;border-bottom:1px solid #e8eff4;">${i + 1}</td>
            <td style="border-bottom:1px solid #cbd5e1;padding:12px 10px;font-size:11pt;vertical-align:middle;"></td>
            <td style="width:240px;border-bottom:1px solid #e8eff4;vertical-align:middle;">
                <div style="display:flex;gap:3px;justify-content:center;">
                    ${[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(v => `<div style="width:22px;height:22px;border:1.5px solid #b8c9d4;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:8pt;font-weight:600;color:#5c7d91;">${v}</div>`).join('')}
                </div>
            </td>
        </tr>`).join('');

    const content = `
    <div class="page">
        <div class="doc-header">
            <h1>🎯 Ruota della Vita</h1>
            <div class="brand">AwarenessTool</div>
        </div>
        <div style="display:flex;gap:12px;margin-bottom:8px;">
            <div style="flex:1;">
                <div style="font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:${BRAND.soft};margin-bottom:2px;">Nome e Cognome</div>
                <div style="border-bottom:1px solid #c2d5e0;min-height:18px;font-size:10pt;font-weight:600;color:#1e293b;padding-left:2px;">${info.name || ''}</div>
            </div>
            <div style="width:120px;">
                <div style="font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:${BRAND.soft};margin-bottom:2px;">Data</div>
                <div style="border-bottom:1px solid #c2d5e0;min-height:18px;font-size:10pt;font-weight:600;color:#1e293b;padding-left:2px;">${info.date || ''}</div>
            </div>
        </div>
        <p style="font-size:9pt;color:${BRAND.soft};margin-bottom:4px;font-style:italic;">Scrivi il nome di ogni area accanto al numero, segna il punteggio (1-10) e collega i punti.</p>
        ${chartSVG}
    </div>

    <div class="page">
        <div class="doc-header">
            <h1>📝 Le mie 8 Aree</h1>
            <div class="brand">AwarenessTool</div>
        </div>
        <p style="font-size:10pt;color:#475569;margin-bottom:20px;line-height:1.8;">
            Scrivi il nome di ciascuna area della tua vita che desideri valutare.<br/>
            Per ogni area, cerchia il punteggio da <strong>1</strong> (per niente soddisfatto/a) a <strong>10</strong> (completamente soddisfatto/a).
        </p>
        <table style="width:100%;border-collapse:collapse;margin-bottom:32px;">
            <thead><tr>
                <th style="width:36px;padding-bottom:10px;"></th>
                <th style="text-align:left;font-size:8pt;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:${BRAND.soft};padding-bottom:10px;">Area della vita</th>
                <th style="text-align:center;font-size:8pt;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:${BRAND.soft};padding-bottom:10px;">Soddisfazione (1-10)</th>
            </tr></thead>
            <tbody>${areaRows}</tbody>
        </table>

        <div style="margin-top:24px;">
            <div style="font-size:12pt;font-weight:700;color:${BRAND.primary};margin-bottom:18px;padding-bottom:6px;border-bottom:1.5px solid ${BRAND.light};display:flex;align-items:center;gap:8px;">
                <span style="font-size:14pt;">💭</span> Riflessioni
            </div>
            <div style="margin-bottom:26px;">
                <p style="font-size:10pt;font-weight:600;color:#334155;margin-bottom:10px;line-height:1.8;">
                    <span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;background:${BRAND.primary};color:white;border-radius:50%;font-size:9pt;font-weight:700;margin-right:8px;">1</span>
                    Guardando la tua Ruota, quale area ti colpisce di più? Perché?
                </p>
                <div style="border:1px solid #e2e8f0;border-radius:6px;min-height:70px;"></div>
            </div>
            <div style="margin-bottom:26px;">
                <p style="font-size:10pt;font-weight:600;color:#334155;margin-bottom:10px;line-height:1.8;">
                    <span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;background:${BRAND.primary};color:white;border-radius:50%;font-size:9pt;font-weight:700;margin-right:8px;">2</span>
                    In quale area vorresti investire più energia nei prossimi mesi?
                </p>
                <div style="border:1px solid #e2e8f0;border-radius:6px;min-height:70px;"></div>
            </div>
            <div style="margin-bottom:26px;">
                <p style="font-size:10pt;font-weight:600;color:#334155;margin-bottom:10px;line-height:1.8;">
                    <span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;background:${BRAND.primary};color:white;border-radius:50%;font-size:9pt;font-weight:700;margin-right:8px;">3</span>
                    Qual è un primo piccolo passo concreto che potresti fare in quell'area?
                </p>
                <div style="border:1px solid #e2e8f0;border-radius:6px;min-height:70px;"></div>
            </div>
        </div>
    </div>`;

    return generatePrintableHTML('Ruota della Vita', content);
};

const generateScritturaStoria = (info = {}) => {
    const content = `
    <div class="page">
        <div class="doc-header">
            <h1>📖 Scrittura della Mia Storia</h1>
            <div class="brand">AwarenessTool</div>
        </div>
        <div class="client-info">
            <div class="field">
                <span class="field-label">Nome e Cognome</span>
                <div class="field-value">${info.name || ''}</div>
            </div>
            <div class="field">
                <span class="field-label">Data</span>
                <div class="field-value">${info.date || ''}</div>
            </div>
        </div>

        <div class="story-prompt">
            <p>
                <strong>Scrivi la tua storia liberamente.</strong><br/>
                Non esiste un modo giusto o sbagliato. Scrivi ciò che senti, come lo senti.
                Puoi partire da dove vuoi: dal presente, dal passato, da un ricordo, da un'emozione.
                Lascia che le parole vengano da sole.
            </p>
        </div>

        <div style="margin-top:20px;margin-bottom:16px;">
            <div style="font-size:10pt;font-weight:700;color:${BRAND.primary};margin-bottom:14px;padding-bottom:4px;border-bottom:1.5px solid ${BRAND.light};">💡 Suggerimenti per iniziare</div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
                <div style="background:${BRAND.bg};border-radius:8px;padding:12px 14px;">
                    <div style="font-size:8pt;font-weight:700;color:${BRAND.primary};text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;">🏞️ Ambientazione</div>
                    <p style="font-size:9pt;color:#475569;line-height:1.7;">Dove si svolge la tua storia? Un luogo, una casa, una città. Descrivi l'atmosfera, i colori, i suoni.</p>
                </div>
                <div style="background:${BRAND.bg};border-radius:8px;padding:12px 14px;">
                    <div style="font-size:8pt;font-weight:700;color:${BRAND.primary};text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;">👥 Personaggi</div>
                    <p style="font-size:9pt;color:#475569;line-height:1.7;">Chi sono le persone importanti della tua storia? Che ruolo hanno avuto? Come li descriveresti?</p>
                </div>
                <div style="background:${BRAND.bg};border-radius:8px;padding:12px 14px;">
                    <div style="font-size:8pt;font-weight:700;color:${BRAND.primary};text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;">🎭 Emozioni</div>
                    <p style="font-size:9pt;color:#475569;line-height:1.7;">Cosa provi quando pensi a questa storia? Quali emozioni emergono? Lasciale uscire.</p>
                </div>
                <div style="background:${BRAND.bg};border-radius:8px;padding:12px 14px;">
                    <div style="font-size:8pt;font-weight:700;color:${BRAND.primary};text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;">🔑 Svolta</div>
                    <p style="font-size:9pt;color:#475569;line-height:1.7;">C'è un momento che ha cambiato tutto? Un prima e un dopo? Racconta cosa è successo.</p>
                </div>
            </div>
        </div>

        <div style="margin-top:16px;padding:10px 14px;background:#fefce8;border-left:3px solid #eab308;border-radius:0 6px 6px 0;font-size:8.5pt;color:#713f12;line-height:1.6;">
            <strong>Domande che possono aiutarti:</strong>
            Com'è iniziata questa storia? • Chi c'era con te? • Cosa hai provato? • Cosa hai imparato? • Cosa faresti diversamente? • Dove ti ha portato?
        </div>
    </div>

    <div class="page">
        <div style="font-size:8pt;font-weight:600;color:${BRAND.soft};text-transform:uppercase;letter-spacing:2px;margin-bottom:16px;">La mia storia</div>
        <div class="story-lines">
            ${Array(24).fill('<div class="story-line"></div>').join('')}
        </div>
    </div>

    <div class="page">
        <div style="font-size:8pt;font-weight:600;color:${BRAND.soft};text-transform:uppercase;letter-spacing:2px;margin-bottom:16px;">La mia storia — continua</div>
        <div class="story-lines">
            ${Array(24).fill('<div class="story-line"></div>').join('')}
        </div>
        <div style="margin-top:24px; display: flex; justify-content: space-between; align-items: flex-end;">
            <div>
                <div style="font-size: 8pt; color: #94a3b8; margin-bottom: 4px;">Firma</div>
                <div style="border-bottom: 1px solid #cbd5e1; width: 200px; height: 30px;"></div>
            </div>
            <div>
                <div style="font-size: 8pt; color: #94a3b8; margin-bottom: 4px;">Data</div>
                <div style="border-bottom: 1px solid #cbd5e1; width: 150px; height: 30px;"></div>
            </div>
        </div>
    </div>`;

    return generatePrintableHTML('Scrittura della Mia Storia', content);
};

// ============================================================
// TOOLKIT CARDS DATA
// ============================================================
const toolkitItems = [
    {
        id: 'questionario-inizio',
        title: 'Questionario di Inizio Percorso',
        description: 'Questionario completo da compilare durante il primo colloquio. Include anagrafica, motivazioni, stato attuale e risorse.',
        icon: '🌱',
        color: 'from-emerald-50 to-teal-50',
        borderColor: 'border-emerald-200',
        iconBg: 'bg-emerald-100',
        textColor: 'text-emerald-800',
        buttonColor: 'bg-emerald-600 hover:bg-emerald-700',
        pages: 2,
        sections: ['Anagrafica', 'Motivazione', 'Qui ed Ora', 'Risorse'],
        generate: (info) => generateQuestionarioInizio(info)
    },
    {
        id: 'questionario-fine',
        title: 'Questionario di Fine Percorso',
        description: 'Valutazione conclusiva del percorso. Include bilancio dei cambiamenti, momenti chiave e prospettive future.',
        icon: '🌳',
        color: 'from-amber-50 to-orange-50',
        borderColor: 'border-amber-200',
        iconBg: 'bg-amber-100',
        textColor: 'text-amber-800',
        buttonColor: 'bg-amber-600 hover:bg-amber-700',
        pages: 2,
        sections: ['Valutazione', 'Cambiamenti', 'Momenti Chiave', 'Futuro'],
        generate: (info) => generateQuestionarioFine(info)
    },
    {
        id: 'ruota-vita',
        title: 'Template Ruota della Vita',
        description: 'Grafico radar con 8 aree libere: il cliente sceglie e scrive le proprie aree. Stampabile, poi i dati si inseriscono in piattaforma.',
        icon: '🎯',
        color: 'from-blue-50 to-indigo-50',
        borderColor: 'border-blue-200',
        iconBg: 'bg-blue-100',
        textColor: 'text-blue-800',
        buttonColor: 'bg-blue-600 hover:bg-blue-700',
        pages: 2,
        sections: ['Aree Libere', 'Grafico Radar', 'Riflessioni'],
        generate: (info) => generateRuotaDellaVita(info)
    },
    {
        id: 'scrittura-storia',
        title: 'Template Scrittura Storia',
        description: 'Spazio libero per raccontare la propria storia. Include suggerimenti su ambientazione, personaggi, emozioni e domande d\'aiuto.',
        icon: '📖',
        color: 'from-purple-50 to-fuchsia-50',
        borderColor: 'border-purple-200',
        iconBg: 'bg-purple-100',
        textColor: 'text-purple-800',
        buttonColor: 'bg-purple-600 hover:bg-purple-700',
        pages: 3,
        sections: ['Suggerimenti', 'Scrittura Libera'],
        generate: (info) => generateScritturaStoria(info)
    }
];

// ============================================================
// COMPONENT
// ============================================================
const ToolkitView = ({ clients = [], counselor = null, counselorsList = [] }) => {
    const [downloadingId, setDownloadingId] = useState(null);
    const [photos, setPhotos] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState('');
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const fileInputRef = useRef(null);

    // Editable Anagrafica state for PDF printing
    const [clientInfo, setClientInfo] = useState({
        name: '',
        date: new Date().toLocaleDateString('it-IT'),
        age: '',
        profession: '',
        contact: '',
        startDate: '',
        totalMeetings: ''
    });

    const handleClientChange = async (clientId) => {
        setSelectedClientId(clientId);
        if (!clientId) {
            setClientInfo({
                name: '',
                date: new Date().toLocaleDateString('it-IT'),
                age: '',
                profession: '',
                contact: '',
                startDate: '',
                totalMeetings: ''
            });
            return;
        }

        const client = clients.find(c => c.id === clientId);
        if (client) {
            const formattedStart = client.createdAt 
                ? new Date(client.createdAt).toLocaleDateString('it-IT') 
                : (client.startDate || '');
            
            const initialInfo = {
                name: client.name || '',
                date: new Date().toLocaleDateString('it-IT'),
                age: client.age || client.eta || '',
                profession: client.profession || client.professione || client.occupation || '',
                contact: client.phone || client.telefono || client.email || '',
                startDate: formattedStart,
                totalMeetings: client.meetingsCount ? String(client.meetingsCount) : ''
            };
            setClientInfo(initialInfo);

            // Fetch actual meetings count if available
            try {
                const meetings = await DataManager.getClientMeetings(client.id);
                if (meetings && Array.isArray(meetings) && meetings.length > 0) {
                    setClientInfo(prev => ({
                        ...prev,
                        totalMeetings: String(meetings.length),
                        startDate: prev.startDate || new Date(meetings[meetings.length - 1].date || meetings[0].date).toLocaleDateString('it-IT')
                    }));
                }
            } catch (e) {
                console.error("Errore recupero incontri cliente:", e);
            }
        }
    };

    const handleDownload = (item) => {
        setDownloadingId(item.id);
        try {
            const html = item.generate(clientInfo);
            const clientSuffix = clientInfo.name ? `_${clientInfo.name.replace(/[^a-zA-Z0-9_-]/g, '_')}` : '';
            downloadPDF(html, `${item.id}${clientSuffix}.pdf`);
        } catch (err) {
            console.error('Errore generazione documento:', err);
            alert('Errore nella generazione del documento: ' + err.message);
        } finally {
            setTimeout(() => setDownloadingId(null), 1000);
        }
    };

    const handlePhotoUpload = (e) => {
        const files = Array.from(e.target.files);
        files.forEach(file => {
            const reader = new FileReader();
            reader.onload = (ev) => {
                setPhotos(prev => [...prev, {
                    id: Date.now() + Math.random(),
                    name: file.name,
                    data: ev.target.result,
                    date: new Date().toLocaleDateString('it-IT'),
                    size: (file.size / 1024).toFixed(0) + ' KB'
                }]);
            };
            reader.readAsDataURL(file);
        });
        e.target.value = '';
    };

    const savePhotoToDevice = (photo) => {
        const link = document.createElement('a');
        link.href = photo.data;
        link.download = `toolkit_${photo.name}`;
        link.click();
    };

    const removePhoto = (id) => {
        setPhotos(prev => prev.filter(p => p.id !== id));
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="bg-white rounded-3xl border border-slate-100 p-8">
                <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <img src="/logo.png" alt="Logo" className="h-10 object-contain" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-black text-slate-800">AwarenessTool <span className="font-light text-slate-400">Toolkit</span></h2>
                        <p className="text-slate-500 mt-1 text-sm leading-relaxed max-w-2xl">
                            Strumenti professionali stampabili per il tuo lavoro di counseling.
                            Ogni documento è formattato in formato A4, pronto per essere stampato o salvato come PDF.
                        </p>
                    </div>
                </div>
            </div>

            {/* Counseling di Gruppo Feature Card */}
            <div className="bg-gradient-to-r from-teal-900 via-slate-900 to-teal-950 text-white rounded-3xl p-6 sm:p-8 shadow-xl relative overflow-hidden border border-teal-800/50">
                <div className="absolute right-0 top-0 translate-x-8 -translate-y-8 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl pointer-events-none"></div>
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
                    <div className="flex items-start gap-4">
                        <div className="w-16 h-16 bg-teal-500/20 border border-teal-400/30 rounded-2xl flex items-center justify-center text-3xl shadow-inner flex-shrink-0">
                            👥
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <span className="bg-teal-400/20 text-teal-300 text-[10px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full border border-teal-400/30">
                                    Strumento Clinico
                                </span>
                            </div>
                            <h3 className="text-xl sm:text-2xl font-black tracking-tight text-white">
                                Counseling di Gruppo
                            </h3>
                            <p className="text-teal-100/80 text-xs sm:text-sm mt-1 max-w-xl leading-relaxed">
                                Redigi, salva e archivia i mini-report per gli incontri di gruppo: seleziona i clienti partecipanti, annota i temi emersi, le tecniche utilizzate e gli esercizi assegnati a casa.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 w-full md:w-auto">
                        <button
                            type="button"
                            onClick={() => setShowGroupModal(true)}
                            className="flex-1 md:flex-none px-6 py-3.5 bg-teal-500 hover:bg-teal-400 text-slate-950 font-black text-xs sm:text-sm uppercase tracking-wider rounded-2xl transition-all shadow-lg hover:shadow-teal-500/20 active:scale-95 flex items-center justify-center gap-2"
                        >
                            <span>📝</span>
                            <span>Apri Counseling di Gruppo</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Client Selector & Editable Anagrafica Panel */}
            <div className="bg-gradient-to-br from-brand-50/70 to-slate-50 border-2 border-brand-200 rounded-3xl p-6 mb-6 animate-fade-in shadow-sm space-y-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3.5">
                        <div className="w-12 h-12 bg-brand-100 text-brand-900 rounded-2xl flex items-center justify-center text-2xl shadow-sm flex-shrink-0">
                            👤
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="font-black text-brand-950 text-sm sm:text-base uppercase tracking-wider">
                                    Associa a un Cliente
                                </h3>
                                {selectedClientId && (
                                    <span className="bg-emerald-100 text-emerald-800 text-[10px] font-extrabold px-2 py-0.5 rounded-full border border-emerald-300 uppercase">
                                        Autocompilato ✓
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-brand-700/90 mt-0.5">
                                Seleziona un cliente per autocompilare l'anagrafica nei PDF e salvare i documenti direttamente nella sua cartella.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <select 
                            className="p-3 bg-white border-2 border-brand-300 rounded-2xl font-bold text-brand-950 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-600 min-w-[240px] shadow-sm text-sm"
                            value={selectedClientId}
                            onChange={(e) => handleClientChange(e.target.value)}
                        >
                            <option value="">-- Nessun Cliente (Compilazione Manuale) --</option>
                            {[...clients].sort((a,b) => (a.name || a.id || '').localeCompare(b.name || b.id || '')).map(c => (
                                <option key={c.id} value={c.id}>{c.name || 'Cliente senza nome'}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Editable Anagrafica Fields */}
                <div className="bg-white/90 p-4 sm:p-5 rounded-2xl border border-brand-200/80 shadow-inner space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-xs font-black uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                            <span>📋</span>
                            <span>Dati Anagrafici per la Stampa (Modificabili)</span>
                        </span>
                        <span className="text-[11px] text-slate-500 italic">
                            I dati qui sotto vengono inseriti automaticamente nell'intestazione dei PDF stampabili
                        </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 pt-1">
                        <div>
                            <label className="block text-[11px] font-bold text-slate-600 mb-1">
                                Nome e Cognome
                            </label>
                            <input
                                type="text"
                                placeholder="Es. Mario Rossi"
                                value={clientInfo.name}
                                onChange={e => setClientInfo({ ...clientInfo, name: e.target.value })}
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold text-slate-600 mb-1">
                                Data Documento
                            </label>
                            <input
                                type="text"
                                placeholder="Es. 24/09/2026"
                                value={clientInfo.date}
                                onChange={e => setClientInfo({ ...clientInfo, date: e.target.value })}
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold text-slate-600 mb-1">
                                Età
                            </label>
                            <input
                                type="text"
                                placeholder="Es. 34"
                                value={clientInfo.age}
                                onChange={e => setClientInfo({ ...clientInfo, age: e.target.value })}
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold text-slate-600 mb-1">
                                Professione
                            </label>
                            <input
                                type="text"
                                placeholder="Es. Insegnante, Libero prof."
                                value={clientInfo.profession}
                                onChange={e => setClientInfo({ ...clientInfo, profession: e.target.value })}
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-[11px] font-bold text-slate-600 mb-1">
                                Contatto (Email / Telefono)
                            </label>
                            <input
                                type="text"
                                placeholder="Es. mario.rossi@email.com o 3331234567"
                                value={clientInfo.contact}
                                onChange={e => setClientInfo({ ...clientInfo, contact: e.target.value })}
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold text-slate-600 mb-1">
                                Percorso Iniziato Il
                            </label>
                            <input
                                type="text"
                                placeholder="Es. 10/01/2026"
                                value={clientInfo.startDate}
                                onChange={e => setClientInfo({ ...clientInfo, startDate: e.target.value })}
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold text-slate-600 mb-1">
                                N° Incontri Totali
                            </label>
                            <input
                                type="text"
                                placeholder="Es. 8"
                                value={clientInfo.totalMeetings}
                                onChange={e => setClientInfo({ ...clientInfo, totalMeetings: e.target.value })}
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Cards Grid */}
            <div className="grid md:grid-cols-2 gap-5">
                {toolkitItems.map(item => (
                    <div key={item.id} className={`bg-white rounded-3xl border-2 ${item.borderColor} overflow-hidden hover:shadow-xl hover:shadow-slate-100/80 transition-all duration-300 group hover:-translate-y-1`}>
                        <div className={`bg-gradient-to-br ${item.color} p-6 pb-5`}>
                            <div className="flex items-center gap-4 mb-3">
                                <div className={`w-12 h-12 ${item.iconBg} rounded-2xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-300`}>{item.icon}</div>
                                <div>
                                    <h3 className={`font-black text-base ${item.textColor}`}>{item.title}</h3>
                                    <span className="text-xs text-slate-400 font-medium">{item.pages} pagine A4</span>
                                </div>
                            </div>
                        </div>
                        <div className="p-6 pt-4">
                            <p className="text-sm text-slate-500 leading-relaxed mb-4">{item.description}</p>
                            <div className="flex flex-wrap gap-1.5 mb-5">
                                {item.sections.map(section => (
                                    <span key={section} className="px-2.5 py-1 bg-slate-50 text-slate-500 text-[10px] font-bold uppercase tracking-widest rounded-lg border border-slate-100">{section}</span>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => handleDownload(item)} disabled={downloadingId === item.id}
                                    className={`flex-1 py-3.5 ${item.buttonColor} text-white rounded-2xl font-bold transition-all duration-200 flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-60 shadow-lg shadow-slate-200/50`}>
                                    {downloadingId === item.id ? (<><span className="animate-spin">⏳</span>...</>) : (<><span>📄</span>Stampa PDF</>)}
                                </button>
                                {selectedClientId && (
                                    <button 
                                        onClick={async () => {
                                            if (isSaving) return;
                                            setIsSaving(true);
                                            try {
                                                await DataManager.saveCollectedDocument(selectedClientId, {
                                                    documentType: item.id,
                                                    documentTitle: item.title,
                                                    category: 'PDF_TEMPLATE',
                                                    savedAt: new Date().toISOString()
                                                });
                                                alert(`✅ Notificato salvataggio template "${item.title}" nella scheda cliente.`);
                                            } catch (err) {
                                                alert("Errore salvataggio: " + err.message);
                                            } finally {
                                                setIsSaving(false);
                                            }
                                        }}
                                        className="w-14 h-14 bg-slate-100 text-slate-400 rounded-2xl border-2 border-dashed border-slate-200 hover:bg-brand-50 hover:text-brand-600 hover:border-brand-300 transition-all flex items-center justify-center text-xl shadow-sm"
                                        title="Registra generazione in scheda cliente"
                                    >
                                        ➕
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Photo Upload Section */}
            <div className="bg-white rounded-3xl border-2 border-slate-100 overflow-hidden">
                <div className="bg-gradient-to-br from-slate-50 to-gray-50 p-6">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-slate-200 rounded-2xl flex items-center justify-center text-2xl">📸</div>
                        <div>
                            <h3 className="font-black text-base text-slate-800">Foto Questionari Compilati</h3>
                            <p className="text-xs text-slate-400 font-medium">Scatta o carica una foto dei fogli compilati dal cliente</p>
                        </div>
                    </div>
                </div>
                <div className="p-6">
                    <input ref={fileInputRef} type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={handlePhotoUpload} />
                    <div className="flex gap-3 mb-5">
                        <button onClick={() => fileInputRef.current?.click()}
                            className="flex-1 py-3.5 bg-slate-800 text-white rounded-2xl font-bold transition hover:bg-slate-900 flex items-center justify-center gap-2 active:scale-[0.98]">
                            <span>📷</span> Scatta / Carica Foto
                        </button>
                    </div>

                    {photos.length > 0 && (
                        <div className="space-y-3">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{photos.length} foto caricate</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {photos.map(photo => (
                                    <div key={photo.id} className="relative group rounded-2xl overflow-hidden border border-slate-100 hover:shadow-lg transition">
                                        <img src={photo.data} alt={photo.name} className="w-full h-40 object-cover" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition" />
                                        <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition">
                                            <p className="text-white text-[10px] font-bold truncate">{photo.name}</p>
                                            <p className="text-white/60 text-[9px]">{photo.date} • {photo.size}</p>
                                            <div className="flex gap-1.5 mt-2">
                                                {selectedClientId && (
                                                    <button 
                                                        onClick={async () => {
                                                            if (isSaving) return;
                                                            setIsSaving(true);
                                                            try {
                                                                await DataManager.saveCollectedDocument(selectedClientId, {
                                                                    photoData: photo.data,
                                                                    photoName: photo.name,
                                                                    category: 'PHOTO_DOCUMENT',
                                                                    uploadedAt: new Date().toISOString()
                                                                });
                                                                alert("✅ Foto salvata nella scheda del cliente!");
                                                                removePhoto(photo.id);
                                                            } catch (err) {
                                                                alert("Errore salvataggio: " + err.message);
                                                            } finally {
                                                                setIsSaving(false);
                                                            }
                                                        }}
                                                        className="flex-1 py-1.5 bg-brand-600/80 backdrop-blur text-white text-[10px] font-bold rounded-lg hover:bg-brand-500 transition shadow-sm"
                                                    >
                                                        💾 Salva in Scheda
                                                    </button>
                                                )}
                                                {!selectedClientId && (
                                                    <button onClick={() => savePhotoToDevice(photo)} className="flex-1 py-1.5 bg-white/20 backdrop-blur text-white text-[10px] font-bold rounded-lg hover:bg-white/30 transition">💾 Download</button>
                                                )}
                                                <button onClick={() => removePhoto(photo.id)} className="py-1.5 px-2.5 bg-red-500/30 backdrop-blur text-white text-[10px] font-bold rounded-lg hover:bg-red-500/50 transition">✕</button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {photos.length === 0 && (
                        <div className="text-center py-8 text-slate-300">
                            <span className="text-4xl block mb-2">📱</span>
                            <p className="text-sm font-medium">Nessuna foto caricata</p>
                            <p className="text-xs mt-1">Usa il pulsante sopra per scattare o caricare foto</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Usage Tip */}
            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 flex items-start gap-3">
                <span className="text-lg flex-shrink-0">💡</span>
                <div>
                    <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1">Suggerimento</p>
                    <p className="text-sm text-slate-500 leading-relaxed">
                        Dopo aver cliccato "Scarica / Stampa PDF", si aprirà l'anteprima di stampa.
                        Per salvare come PDF, seleziona "Salva come PDF" dalla lista stampanti.
                        Puoi anche fotografare i questionari compilati per salvarli digitalmente.
                    </p>
                </div>
            </div>
            {/* Group Counseling Modal */}
            {showGroupModal && (
                <GroupCounselingModal
                    clients={clients}
                    counselor={counselor}
                    counselorsList={counselorsList}
                    onClose={() => setShowGroupModal(false)}
                />
            )}
        </div>
    );
};

export default ToolkitView;
